import PageHero from '@/components/PageHero';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  CreditCard, 
  CheckCircle, 
  Star,
  BookOpen,
  Award,
  TrendingUp
} from 'lucide-react';

const Preise = () => {
  const modulePrice = 360;
  const examFee = 60;
  const totalPrice = modulePrice * 5 + examFee;

  const paymentOptions = [
    {
      title: "Gesamtzahlung",
      price: 1800,
      originalPrice: totalPrice,
      savings: examFee,
      description: "Gesamter Lehrgang + Prüfung",
      features: ["Alle 5 Module", "Prüfungsgebühr inkludiert", "Ersparnis von €60"],
      popular: true
    },
    {
      title: "2 Teilzahlungen",
      price: "2x €930",
      description: "Aufgeteilt in 2 Raten",
      features: ["Alle 5 Module", "Prüfungsgebühr inkludiert", "Flexible Zahlung"]
    },
    {
      title: "12 Monatsraten",
      price: "12x €155",
      description: "Monatliche Ratenzahlung",
      features: ["Alle 5 Module", "Prüfungsgebühr inkludiert", "Kleine monatliche Beträge"]
    }
  ];

  const moduleOptions = [
    { number: 1, price: modulePrice, included: "Skripten inklusive" },
    { number: 2, price: modulePrice, included: "Skripten inklusive" },
    { number: 3, price: modulePrice, included: "Skripten inklusive" },
    { number: 4, price: modulePrice, included: "Skripten inklusive" },
    { number: 5, price: modulePrice, included: "Skripten inklusive", hasExam: true }
  ];

  return (
    <>
      <PageHero 
        title="Preise Ausbildung" 
        description="Transparente Preise für Ihre spirituelle Ausbildung"
        image="https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05"
      />
      
      <section className="py-20" style={{ backgroundColor: '#F6E9B2' }}>
        <div className="container mx-auto px-4">
          {/* Payment Packages */}
          <div className="mb-16 bg-primary-lightest py-16 -mx-4 px-4 rounded-3xl">
            <h2 className="text-3xl font-bold text-primary-deep text-center mb-12">
              Zahlungsmöglichkeiten
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {paymentOptions.map((option, index) => (
                <GlassCard 
                  key={index} 
                  className={`p-8 relative flex flex-col bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300 ${option.popular ? 'ring-2 ring-primary-main' : ''}`}
                >
                  {option.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-primary-main text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-1">
                        <Star className="h-4 w-4" />
                        <span>Beliebt</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center mb-6 grow">
                    <h3 className="text-xl font-bold text-primary-deep mb-2">
                      {option.title}
                    </h3>
                    <div className="text-3xl font-bold text-gray-800 mb-2">
                      {typeof option.price === 'string' ? option.price : `€${option.price}`}
                    </div>
                    {option.savings && (
                      <div className="text-sm text-green-600 font-medium">
                        Ersparnis: €{option.savings}
                      </div>
                    )}
                    <p className="text-gray-600">{option.description}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-8 grow">
                    {option.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-primary-main" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    asChild 
                    className={`w-full mt-auto ${
                      option.popular 
                        ? 'bg-primary-main hover:bg-primary-deep' 
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    <Link to="/anmeldung">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Auswählen
                    </Link>
                  </Button>
                </GlassCard>
              ))}
            </div>
          </div>

          {/* Individual Module Prices */}
          <div className="mb-16 bg-white/90 py-16 -mx-4 px-4">
            <h2 className="text-3xl font-bold text-primary-deep text-center mb-12">
              Einzelne Module
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <GlassCard className="p-8 bg-primary-lightest border-primary-lightest">
                <div className="space-y-4">
                  {moduleOptions.map((module) => (
                    <div 
                      key={module.number} 
                      className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-primary-main text-white rounded-full flex items-center justify-center font-bold">
                          {module.number}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">
                            Modul {module.number}
                          </div>
                          <div className="text-sm text-gray-600">{module.included}</div>
                          {module.hasExam && (
                            <div className="text-sm text-green-600 font-medium">
                              + Prüfungsgebühr €{examFee}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary-deep">
                          €{module.price}
                        </div>
                        {module.hasExam && (
                          <div className="text-sm text-gray-600">
                            (+ €{examFee} Prüfung)
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </div>

          {/* Additional Module */}
          <div className="mb-16 bg-white/90 py-16 -mx-4 px-4">
            <GlassCard className="p-8 max-w-2xl mx-auto bg-orange-50 border-orange-200">
              <div className="text-center">
                <Award className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-orange-800 mb-2">
                  NEU: "Der Lebensgarten"
                </h3>
                <div className="text-3xl font-bold text-orange-600 mb-2">€360</div>
                <p className="text-orange-700 mb-4">
                  Zusatzmodul nur für Absolventen aller Module
                </p>
                <Button 
                  asChild 
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Link to="/anmeldung">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Mehr erfahren
                  </Link>
                </Button>
              </div>
            </GlassCard>
          </div>

          {/* Benefits Section */}
          <div className="mb-16 bg-primary-lightest py-16 -mx-4 px-4 rounded-3xl">
            <h2 className="text-3xl font-bold text-primary-deep text-center mb-12">
              Warum die Module in der Reihenfolge absolvieren?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <GlassCard className="p-6 text-center bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300">
                <TrendingUp className="h-12 w-12 text-primary-main mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary-deep mb-3">
                  Optimale Lernerfahrung
                </h3>
                <p className="text-gray-700">
                  Das Wissen aus den vorherigen Modulen wird in den nachfolgenden 
                  Modulen vertieft und erweitert.
                </p>
              </GlassCard>
              
              <GlassCard className="p-6 text-center bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300">
                <CheckCircle className="h-12 w-12 text-primary-main mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary-deep mb-3">
                  Verbessertes Verständnis
                </h3>
                <p className="text-gray-700">
                  Neue Inhalte können besser verstanden und verinnerlicht werden, 
                  da sie auf dem Wissen des vorherigen Moduls aufbauen.
                </p>
              </GlassCard>
              
              <GlassCard className="p-6 text-center bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300">
                <Award className="h-12 w-12 text-primary-main mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-primary-deep mb-3">
                  Bessere Vorbereitung
                </h3>
                <p className="text-gray-700">
                  Wenn du die Module in der vorgegebenen Reihenfolge absolvierst, 
                  bist du optimal auf das Aufbaumodul vorbereitet.
                </p>
              </GlassCard>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mb-16">
            <GlassCard className="p-8 max-w-4xl mx-auto bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300">
              <h3 className="text-2xl font-bold text-primary-deep text-center mb-6">
                Flexible Zahlungsoptionen
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    Akzeptierte Zahlungsmethoden:
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-main" />
                      <span>Überweisung</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-main" />
                      <span>Zahlung vor Ort</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-primary-main" />
                      <span>Kombination aus beiden</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    Persönliche Beratung:
                  </h4>
                  <p className="text-gray-700 mb-4">
                    Du kannst einzelne Module separat bezahlen. Gerne berate ich dich, 
                    welche Option für dich die beste ist.
                  </p>
                  <Button asChild className="bg-primary-main hover:bg-primary-deep">
                    <Link to="/anmeldung">
                      <Calendar className="mr-2 h-4 w-4" />
                      Beratung vereinbaren
                    </Link>
                  </Button>
                </div>
              </div>
            </GlassCard>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <GlassCard className="p-8 max-w-2xl mx-auto bg-white/90 border-white/30 hover:bg-white/95 transition-all duration-300">
              <h3 className="text-2xl font-bold text-primary-deep mb-4">
                Bereit für Ihre Ausbildung?
              </h3>
              <p className="text-gray-700 mb-6">
                Wählen Sie die für Sie passende Zahlungsoption und starten Sie Ihre 
                spirituelle Reise
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary-main hover:bg-primary-deep">
                  <Link to="/anmeldung">
                    <Calendar className="mr-2 h-5 w-5" />
                    Jetzt anmelden
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary-main text-primary-main hover:bg-primary-main hover:text-white">
                  <Link to="/agb">
                    <BookOpen className="mr-2 h-5 w-5" />
                    AGB lesen
                  </Link>
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>
    </>
  );
};

export default Preise;